import React, { useState, useEffect, useCallback } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import { PageHeader, StatCards, TabBar, Field, inputStyle, btnSm, Empty } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';
import { getBrandingSlug } from '../utils/formatters';

// Color coding by action type
const ACTION_COLORS = {
  login:'#3498db', logout:'#888',
  order_created:'#2ecc71', order_voided:'#e74c3c',
  bill_created:'#2ecc71', bill_paid:'#27ae60', bill_reopened:'#e67e22',
  payment_requested:'#3498db', discount_applied:'#e67e22', refund_issued:'#e74c3c',
  menu_item_created:'#2ecc71', menu_item_updated:'#3498db', menu_item_deleted:'#e74c3c', price_changed:'#e67e22',
  gift_sale_created:'#9b59b6', gift_sale_voided:'#e74c3c', gift_item_restocked:'#2ecc71', gift_payment_confirmed:'#27ae60',
  guest_checkin:'#2ecc71', guest_checkout:'#e67e22', folio_charge_posted:'#3498db',
  conf_booking_created:'#9b59b6', conf_booking_confirmed:'#2ecc71',
  user_created:'#3498db', user_updated:'#e67e22', password_changed:'#e67e22',
  export_excel:'#c8a96e', export_pdf:'#c8a96e', rollover:'#9b59b6',
  ingredient_price_changed:'#e67e22', stock_adjusted:'#3498db',
  other:'#555',
};

const ROLE_COLORS = {
  admin:'#c8a96e', waiter:'#3498db', cashier:'#2ecc71', kitchen:'#e67e22',
  reception:'#9b59b6', gate:'#e74c3c', housekeeping:'#1abc9c', accountant:'#f39c12',
  gift_shop:'#e91e8c', restaurant_manager:'#00b300',
};

const ALL_ACTIONS = [
  { id:'', label:'All Actions' },
  { id:'login', label:'Login' }, { id:'logout', label:'Logout' },
  { id:'order_created', label:'Order Created' }, { id:'order_voided', label:'Order Voided' },
  { id:'bill_created', label:'Bill Created' }, { id:'bill_paid', label:'Bill Paid' },
  { id:'bill_reopened', label:'Bill Reopened' }, { id:'discount_applied', label:'Discount Applied' },
  { id:'refund_issued', label:'Refund Issued' }, { id:'payment_requested', label:'Payment Requested' },
  { id:'menu_item_created', label:'Menu Added' }, { id:'menu_item_updated', label:'Menu Updated' },
  { id:'menu_item_deleted', label:'Menu Deleted' }, { id:'price_changed', label:'Price Changed' },
  { id:'gift_sale_created', label:'Gift Sale' }, { id:'gift_payment_confirmed', label:'Gift Payment' },
  { id:'gift_item_restocked', label:'Gift Restocked' },
  { id:'guest_checkin', label:'Check-In' }, { id:'guest_checkout', label:'Check-Out' },
  { id:'folio_charge_posted', label:'Folio Charge' },
  { id:'conf_booking_created', label:'Conf. Booking' }, { id:'conf_booking_confirmed', label:'Conf. Confirmed' },
  { id:'user_created', label:'User Created' }, { id:'user_updated', label:'User Updated' },
  { id:'export_excel', label:'Excel Export' }, { id:'export_pdf', label:'PDF Export' },
  { id:'rollover', label:'Daily Rollover' },
  { id:'ingredient_price_changed', label:'Ingredient Price' }, { id:'stock_adjusted', label:'Stock Adjusted' },
];

const ALL_ROLES = ['', 'admin', 'waiter', 'cashier', 'kitchen', 'reception', 'gate', 'housekeeping', 'accountant', 'gift_shop', 'restaurant_manager'];

export default function AuditTrailPage({ notify, isAdmin }) {
  const [logs, setLogs]       = useState([]);
  const [users, setUsers]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail]   = useState(null);

  // Filters
  const [fAction, setFAction]   = useState('');
  const [fRole,   setFRole]     = useState('');
  const [fUser,   setFUser]     = useState('');
  const [fFrom,   setFFrom]     = useState('');
  const [fTo,     setFTo]       = useState('');
  const [fSearch, setFSearch]   = useState('');
  const [tab, setTab]           = useState('log');

  const buildParams = () => {
    let p = '?';
    if (fAction) p += `action=${fAction}&`;
    if (fRole)   p += `role=${fRole}&`;
    if (fUser)   p += `user_id=${fUser}&`;
    if (fFrom)   p += `date_from=${fFrom}&`;
    if (fTo)     p += `date_to=${fTo}&`;
    if (fSearch) p += `search=${encodeURIComponent(fSearch)}&`;
    return p;
  };

  const doExportAudit = async () => {
    const p = buildParams();
    notify('Preparing audit log export…');
    try {
      const res = await axiosClient.get(api.exportAuditLogExcel(p), { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file returned');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_AuditLog_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify('Audit log exported ✓');
    } catch(e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, u] = await Promise.all([
        api.getAuditLogs(buildParams()),
        api.getUsers(),
      ]);
      const logData = l.data.results || l.data;
      setLogs(logData);
      setUsers(u.data.results || u.data);

      // Build summary from loaded logs
      const actionCounts = {};
      const userCounts   = {};
      const roleCounts   = {};
      logData.forEach(lg => {
        actionCounts[lg.action] = (actionCounts[lg.action]||0) + 1;
        const un = lg.user_name || 'System';
        userCounts[un] = (userCounts[un]||0) + 1;
        const ur = lg.user_role || 'system';
        roleCounts[ur] = (roleCounts[ur]||0) + 1;
      });
      setSummary({ total: logData.length, actionCounts, userCounts, roleCounts });
    } catch(e) {
      notify('Error loading audit logs', 'error');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fAction, fRole, fUser, fFrom, fTo, fSearch]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setFAction(''); setFRole(''); setFUser(''); setFFrom(''); setFTo(''); setFSearch('');
  };

  const topActions = summary ? Object.entries(summary.actionCounts).sort((a,b)=>b[1]-a[1]).slice(0,8) : [];
  const topUsers   = summary ? Object.entries(summary.userCounts).sort((a,b)=>b[1]-a[1]).slice(0,8) : [];

  const hasFilters = fAction||fRole||fUser||fFrom||fTo||fSearch;

  return (
    <div>
      <PageHeader icon="🔍" title="Audit Trail" sub="System-wide activity log — who did what, when"/>

      {summary && (
        <StatCards stats={[
          { label:'Events Loaded',   value:summary.total,                                              color:'#3498db' },
          { label:'Unique Actions',  value:Object.keys(summary.actionCounts).length,                   color:'#c8a96e' },
          { label:'Active Users',    value:Object.keys(summary.userCounts).length,                     color:'#2ecc71' },
          { label:'Critical Events', value:(summary.actionCounts['order_voided']||0)+(summary.actionCounts['refund_issued']||0)+(summary.actionCounts['menu_item_deleted']||0), color:'#e74c3c' },
        ]}/>
      )}

      <TabBar tabs={[
        { id:'log',     label:`Activity Log (${logs.length})`, icon:'📋' },
        { id:'insights',label:'Insights',                      icon:'📊' },
      ]} active={tab} onChange={setTab}/>

      {tab==='log' && (
        <>
          {/* Filter bar */}
          <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:'14px 16px', marginBottom:12 }}>
            <div style={{ fontSize:10, color:'#c8a96e', letterSpacing:2, textTransform:'uppercase', marginBottom:10 }}>Filter Audit Log</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10, marginBottom:10 }}>
              <Field label="Action Type">
                <select style={inputStyle} value={fAction} onChange={e=>setFAction(e.target.value)}>
                  {ALL_ACTIONS.map(a=><option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </Field>
              <Field label="Role">
                <select style={inputStyle} value={fRole} onChange={e=>setFRole(e.target.value)}>
                  <option value="">All Roles</option>
                  {ALL_ROLES.filter(r=>r).map(r=><option key={r} value={r}>{r.replace('_',' ')}</option>)}
                </select>
              </Field>
              <Field label="User">
                <select style={inputStyle} value={fUser} onChange={e=>setFUser(e.target.value)}>
                  <option value="">All Users</option>
                  {users.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </Field>
              <Field label="Search Description">
                <input style={inputStyle} placeholder="Keyword search…" value={fSearch} onChange={e=>setFSearch(e.target.value)}/>
              </Field>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:10, alignItems:'flex-end' }}>
              <Field label="Date From">
                <input type="date" style={inputStyle} value={fFrom} onChange={e=>setFFrom(e.target.value)}/>
              </Field>
              <Field label="Date To">
                <input type="date" style={inputStyle} value={fTo} onChange={e=>setFTo(e.target.value)}/>
              </Field>
              <div style={{ display:'flex', gap:8, alignItems:'flex-end', paddingBottom:1 }}>
                <button style={{ ...btnSm, background:'#c8a96e', color:'#111', fontWeight:700, padding:'9px 18px' }} onClick={load}>
                  {loading ? '↻ Loading…' : '🔍 Apply'}
                </button>
                {hasFilters && (
                  <>
                    <button style={{ ...btnSm, padding:'9px 14px', color:'var(--textMuted)' }} onClick={clearFilters}>Clear</button>
                    {isAdmin && <button style={{ ...btnSm, background:'var(--bgSuccess)', color:'#2ecc71', padding:'9px 14px' }} onClick={doExportAudit}>📥 Export Excel</button>}
                  </>
                )}
              </div>
            </div>
          </div>

          {logs.length===0 ? <Empty text="No audit events match your filters"/> : (
            <div style={{ background:'#0f0f0f', border:'1px solid #1e1e1e', borderRadius:4, overflow:'hidden' }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#0e0e0e' }}>
                      {['Timestamp','User','Role','Action','Description','IP','Ref'].map(h=>(
                        <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:9, color:'#444', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap', borderBottom:'1px solid #1a1a1a' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log=>(
                      <tr key={log.id} onClick={()=>setDetail(log)}
                        style={{ borderBottom:'1px solid #111', cursor:'pointer', transition:'background .1s' }}
                        onMouseEnter={e=>e.currentTarget.style.background='#181818'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ padding:'8px 10px', color:'var(--textFaint)', fontSize:11, whiteSpace:'nowrap' }}>
                          {new Date(log.created_at).toLocaleString('en-KE')}
                        </td>
                        <td style={{ padding:'8px 10px', fontWeight:600, color: log.user_name ? (ROLE_COLORS[log.user_role]||'#aaa') : '#555' }}>
                          {log.user_name||'System'}
                        </td>
                        <td style={{ padding:'8px 10px' }}>
                          {log.user_role && (
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:2, background:ROLE_COLORS[log.user_role]+'22'||'var(--bgInput)', color:ROLE_COLORS[log.user_role]||'#555', textTransform:'uppercase', letterSpacing:0.5, fontWeight:700 }}>
                              {log.user_role.replace('_',' ')}
                            </span>
                          )}
                        </td>
                        <td style={{ padding:'8px 10px' }}>
                          <span style={{ fontSize:9, padding:'2px 7px', borderRadius:2, background:(ACTION_COLORS[log.action]||'#555')+'22', color:ACTION_COLORS[log.action]||'#555', textTransform:'uppercase', letterSpacing:0.5, fontWeight:700, whiteSpace:'nowrap' }}>
                            {log.action_label||log.action}
                          </span>
                        </td>
                        <td style={{ padding:'8px 10px', color:'var(--textMuted)', maxWidth:400 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.description}</div>
                        </td>
                        <td style={{ padding:'8px 10px', color:'#444', fontSize:10, fontFamily:'monospace', whiteSpace:'nowrap' }}>
                          {log.ip_address||'—'}
                        </td>
                        <td style={{ padding:'8px 10px', color:'#444', fontSize:10 }}>
                          {log.target_type&&log.target_id ? `${log.target_type}#${log.target_id}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:'8px 12px', borderTop:'1px solid #1a1a1a', fontSize:11, color:'#444', display:'flex', justifyContent:'space-between' }}>
                <span>Showing {logs.length} events (max 500)</span>
                <span>Click any row to view full details</span>
              </div>
            </div>
          )}
        </>
      )}

      {tab==='insights' && summary && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
          {/* Action breakdown */}
          <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:16 }}>
            <div style={{ fontSize:11, color:'#c8a96e', letterSpacing:2, textTransform:'uppercase', marginBottom:14 }}>Action Breakdown</div>
            {topActions.map(([action, count])=>(
              <div key={action} style={{ marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontSize:11, color: ACTION_COLORS[action]||'#888' }}>{action.replace(/_/g,' ')}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--textPrimary)' }}>{count}</span>
                </div>
                <div style={{ height:3, background:'var(--bgInput)', borderRadius:2 }}>
                  <div style={{ height:3, background:ACTION_COLORS[action]||'#555', borderRadius:2, width:`${(count/(topActions[0]?.[1]||1))*100}%` }}/>
                </div>
              </div>
            ))}
          </div>

          {/* User activity */}
          <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:16 }}>
            <div style={{ fontSize:11, color:'#c8a96e', letterSpacing:2, textTransform:'uppercase', marginBottom:14 }}>Most Active Users</div>
            {topUsers.map(([uname, count], i)=>(
              <div key={uname} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderBottom:'1px solid #111' }}>
                <span style={{ fontSize:14, color:'var(--textFaint)', minWidth:20, textAlign:'center' }}>
                  {i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}
                </span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, color:'var(--textPrimary)' }}>{uname}</div>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:'#c8a96e' }}>{count} events</div>
              </div>
            ))}
          </div>

          {/* Role distribution */}
          <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:16 }}>
            <div style={{ fontSize:11, color:'#c8a96e', letterSpacing:2, textTransform:'uppercase', marginBottom:14 }}>Activity by Role</div>
            {Object.entries(summary.roleCounts).sort((a,b)=>b[1]-a[1]).map(([role, count])=>(
              <div key={role} style={{ marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontSize:11, color:ROLE_COLORS[role]||'#888' }}>{role.replace('_',' ')}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--textPrimary)' }}>{count}</span>
                </div>
                <div style={{ height:3, background:'var(--bgInput)', borderRadius:2 }}>
                  <div style={{ height:3, background:ROLE_COLORS[role]||'#555', borderRadius:2, width:`${(count/summary.total)*100}%` }}/>
                </div>
              </div>
            ))}
          </div>

          {/* Security events */}
          <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:16 }}>
            <div style={{ fontSize:11, color:'#c8a96e', letterSpacing:2, textTransform:'uppercase', marginBottom:14 }}>Security & Critical Events</div>
            {[
              ['🔴 Order Voids',       summary.actionCounts['order_voided']||0,        '#e74c3c'],
              ['🟠 Refunds Issued',     summary.actionCounts['refund_issued']||0,       '#e74c3c'],
              ['🟡 Discounts Applied',  summary.actionCounts['discount_applied']||0,    '#e67e22'],
              ['🔵 Bill Reopened',      summary.actionCounts['bill_reopened']||0,       '#3498db'],
              ['🔴 Menu Item Deleted',  summary.actionCounts['menu_item_deleted']||0,   '#e74c3c'],
              ['🟡 Price Changed',      summary.actionCounts['price_changed']||0,       '#e67e22'],
              ['🟢 User Created',       summary.actionCounts['user_created']||0,        '#2ecc71'],
              ['🟡 Password Changed',   summary.actionCounts['password_changed']||0,    '#e67e22'],
              ['🟢 Daily Rollover',     summary.actionCounts['rollover']||0,            '#9b59b6'],
              ['📊 Data Exports',       (summary.actionCounts['export_excel']||0)+(summary.actionCounts['export_pdf']||0), '#c8a96e'],
            ].map(([l,v,c])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #111' }}>
                <span style={{ fontSize:12, color:'var(--textMuted)' }}>{l}</span>
                <span style={{ fontSize:13, fontWeight:700, color: v>0?c:'#444' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:`1px solid ${ACTION_COLORS[detail.action]||'var(--borderMid)'}44`, borderRadius:6, padding:24, maxWidth:560, width:'100%', overflowX:'hidden', maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:18 }}>
              <div>
                <span style={{ fontSize:10, padding:'2px 8px', borderRadius:2, background:(ACTION_COLORS[detail.action]||'#555')+'22', color:ACTION_COLORS[detail.action]||'#555', textTransform:'uppercase', letterSpacing:1, fontWeight:700 }}>
                  {detail.action_label||detail.action}
                </span>
                <div style={{ fontSize:16, fontWeight:700, color:'var(--textPrimary)', marginTop:6 }}>Audit Event #{detail.id}</div>
              </div>
              <button onClick={()=>setDetail(null)} style={{ background:'none', border:'none', color:'var(--textFaint)', fontSize:20, cursor:'pointer' }}>✕</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'8px 14px', marginBottom:14 }}>
              {[
                ['Timestamp', new Date(detail.created_at).toLocaleString('en-KE')],
                ['User', detail.user_name || 'System'],
                ['Role', (detail.user_role||'—').replace('_',' ')],
                ['IP Address', detail.ip_address||'Not recorded'],
                ['Target Type', detail.target_type||'—'],
                ['Target ID', detail.target_id ? `#${detail.target_id}` : '—'],
              ].map(([l,v])=>(
                <div key={l}>
                  <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:3 }}>{l}</div>
                  <div style={{ fontSize:13, color:'var(--textSecond)' }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Description</div>
              <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'10px 12px', fontSize:13, color:'var(--textMuted)', lineHeight:1.5 }}>
                {detail.description}
              </div>
            </div>

            {detail.meta && Object.keys(detail.meta).length > 0 && (
              <div>
                <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Additional Data</div>
                <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'10px 12px', fontSize:11, color:'var(--textMuted)', fontFamily:'monospace', lineHeight:1.6 }}>
                  {Object.entries(detail.meta).map(([k,v])=>(
                    <div key={k}><span style={{ color:'#c8a96e' }}>{k}:</span> {JSON.stringify(v)}</div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
              <button style={{ ...btnSm, padding:'9px 20px' }} onClick={()=>setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}